import { useRouter } from 'expo-router';
import React, { useCallback, useRef, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import { Avatar, Button, HStack, Text, VStack, theme } from '@squire/ui';

import type { WaitlistTimeSlot } from '@/api/waitlist/waitlistTimeSlotsQuery';
import { BellIcon, ClockIcon, ExclamationMarkIcon } from '@/components/_icons';
import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import { CloseButton } from '@/components/_shared/CloseButton';
import { ShopDirections } from '@/components/_shared/ShopDirections';
import { ViewWaitlistCard } from '@/components/waitlist/ViewWaitlistCard';
import { WaitlistTimeSlotsSheet } from '@/components/waitlist/WaitlistTimeSlotsSheet';
import { routes } from '@/constants/routes';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { useWidget } from '@/providers/WidgetProvider';
import { isNative } from '@/utils/getPlatform';

import { styles } from './styles';
import type { ViewWaitlistContentWrapperProps } from './types';
import { useViewWaitlistData } from './useViewWaitlistData';

const ViewWaitlistContentWrapper: React.FC<ViewWaitlistContentWrapperProps> = ({
  waitlistCustomerId,
  slotId: slotIdFromUrl,
}) => {
  const { t } = useScreenTranslations('ViewWaitlistContentWrapper');
  const router = useRouter();
  const { isWidget, closeWidget } = useWidget();

  // Local state
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);

  // Bottom sheet refs
  const timeSlotsSheetRef = useRef<BottomSheetRef>(null);

  const {
    waitlistData,
    shop,
    barber,
    customerName,
    barberName,
    barberPhotoUrl,
    slotTimes,
    uiState,
    selectedSlot,
    formattedDate,
    availableTips,
    canAddTip,
    isBooking,
    isUpdatingTip,
    handleSelectTip,
    handleBook,
  } = useViewWaitlistData({
    waitlistCustomerId,
    slotIdFromUrl,
    selectedSlotId,
  });

  const handleOpenTimeSlots = useCallback(() => {
    timeSlotsSheetRef.current?.open();
  }, []);

  const handleOpenReschedule = useCallback(() => {
    router.push(routes.waitlistReschedule({ waitlistCustomerId }));
  }, [router, waitlistCustomerId]);

  const handleSlotConfirm = useCallback((slot: WaitlistTimeSlot) => {
    setSelectedSlotId(slot.waitingListSlotId);
    timeSlotsSheetRef.current?.close();
  }, []);

  const handleClose = useCallback(() => {
    if (router.canDismiss()) {
      router.dismissAll();
    } else {
      router.replace(routes.home);
    }
  }, [router]);

  const selectedTime = selectedSlot?.time ?? null;

  const renderHeaderContent = () => {
    if (uiState === 'removed') {
      return (
        <>
          <View style={styles.expiredIconContainer}>
            <ExclamationMarkIcon size={16} color="iconNegative" />
          </View>
          <Text
            variant="2xlStrong"
            color="textPrimary"
            style={styles.headerText}
          >
            {t('header.removed')}
          </Text>
        </>
      );
    }

    if (uiState === 'expired') {
      return (
        <>
          <View style={styles.expiredIconContainer}>
            <ExclamationMarkIcon size={16} color="iconNegative" />
          </View>
          <Text
            variant="2xlStrong"
            color="textPrimary"
            style={styles.headerText}
          >
            {t('header.expired', { barber: barberName, date: formattedDate })}
          </Text>
        </>
      );
    }

    if (uiState === 'available') {
      return (
        <>
          <View style={styles.clockIconContainer}>
            <ClockIcon size={16} color="iconPrimary" />
          </View>
          <Text
            variant="2xlStrong"
            color="textPrimary"
            style={styles.headerText}
          >
            {t('header.available', { barber: barberName, date: formattedDate })}
          </Text>
        </>
      );
    }

    if (uiState === 'selected') {
      return (
        <>
          <View style={styles.clockIconContainer}>
            <ClockIcon size={16} color="iconPrimary" />
          </View>
          <Text
            variant="2xlStrong"
            color="textPrimary"
            style={styles.headerText}
          >
            {t('header.readyToBook', {
              barber: barberName,
              date: formattedDate,
            })}
          </Text>
        </>
      );
    }

    // Active (default)
    return (
      <>
        <View style={styles.clockIconContainer}>
          <ClockIcon size={16} color="iconPrimary" />
        </View>
        <Text variant="2xlStrong" color="textPrimary" style={styles.headerText}>
          {customerName
            ? t('confirmation.title', { name: customerName })
            : t('confirmation.titleGeneric')}
        </Text>
      </>
    );
  };

  const renderHeader = () => (
    <HStack space={theme.spacing[4]} style={styles.headerContainer}>
      {renderHeaderContent()}

      {(isNative() || isWidget) && (
        <CloseButton
          onPress={isWidget ? closeWidget : handleClose}
          style={styles.closeButton}
          accessibilityLabel={t('accessibility.close')}
          accessibilityHint={t('accessibility.closeHint')}
          testID="waitlist-header-close-button"
        />
      )}
    </HStack>
  );

  const renderBadge = () => {
    if (uiState === 'active') {
      return (
        <View style={styles.waitlistBadge}>
          <View style={styles.badgeTopRow}>
            <View style={styles.badgeTopText}>
              <Text variant="xlStrong" color="textPrimaryInverse">
                {t('position.title', {
                  position: waitlistData.numberInLine,
                })}
              </Text>
              <Text variant="m" color="textSecondaryInverse">
                {t('position.subtitle', { name: barberName })}
              </Text>
            </View>
            <View style={styles.positionBadge}>
              <Text variant="xlStrong" color="textStatic">
                {waitlistData.numberInLine}
              </Text>
            </View>
          </View>
          <View style={styles.badgeSeparator} />
          <View style={styles.notifyRow}>
            <View style={styles.bellIconContainer}>
              <BellIcon size={15} color="iconPrimaryInverse" />
              <View style={styles.notificationDot} />
            </View>
            <Text variant="m" color="textPrimaryInverse">
              {t('position.notify')}
            </Text>
          </View>
        </View>
      );
    }

    if (uiState === 'available') {
      const slotsCount = slotTimes?.length ?? 0;

      return (
        <View style={styles.waitlistBadge}>
          <VStack space={theme.spacing[1]} style={styles.badgeContent}>
            <View style={styles.badgeHeader}>
              <Text variant="xlStrong" color="textPrimaryInverse">
                {t('badge.waitingList')}
              </Text>
              <Text variant="m" color="textSecondaryInverse">
                {t('badge.slotsAvailable', { count: slotsCount })}
              </Text>
            </View>
            <View style={styles.badgeBarberRowLg}>
              <Avatar
                uri={barberPhotoUrl}
                size="lg"
                shape="square"
                fallbackText={barberName}
                style={styles.badgeAvatar}
              />
              <VStack>
                <Text variant="xlStrong" color="textPrimaryInverse">
                  {barberName}
                </Text>
                <Text variant="l" color="textSecondaryInverse">
                  {waitlistData.services?.[0]?.name ?? ''}
                </Text>
                <Text variant="l" color="textSecondaryInverse">
                  {formattedDate}
                </Text>
              </VStack>
            </View>
            <Pressable
              onPress={handleOpenTimeSlots}
              style={styles.badgeCtaButton}
            >
              <Text variant="lStrong" color="textStatic">
                {t('badge.chooseTime')}
              </Text>
            </Pressable>
          </VStack>
        </View>
      );
    }

    if (uiState === 'expired') {
      return (
        <View style={styles.waitlistBadgeExpired}>
          <VStack space={theme.spacing[1]} style={styles.badgeContent}>
            <Text variant="xlStrong" color="textPrimaryInverse">
              {t('badge.expired')}
            </Text>
            <View style={styles.badgeBarberRowLg}>
              <Avatar
                uri={barberPhotoUrl}
                size="lg"
                shape="square"
                fallbackText={barberName}
                style={styles.badgeAvatar}
              />
              <VStack>
                <Text variant="xlStrong" color="textPrimaryInverse">
                  {barberName}
                </Text>
                <Text variant="l" color="textSecondaryInverse">
                  {waitlistData.services?.[0]?.name ?? ''}
                </Text>
                <Text variant="l" color="textSecondaryInverse">
                  {formattedDate}
                </Text>
              </VStack>
            </View>
            <Pressable
              onPress={handleOpenReschedule}
              style={styles.badgeCtaButton}
            >
              <Text variant="lStrong" color="textStatic">
                {t('badge.chooseDifferentTime')}
              </Text>
            </Pressable>
          </VStack>
        </View>
      );
    }

    return null;
  };

  return (
    <>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <VStack style={styles.content} space={theme.spacing[6]}>
          {renderHeader()}
          {renderBadge()}
          <View style={styles.card}>
            <ViewWaitlistCard
              barber={barber}
              services={waitlistData.services ?? []}
              day={waitlistData.day}
              tipAmount={waitlistData.tipAmount}
              serviceDuration={waitlistData.serviceDuration}
              shop={shop}
              uiState={uiState}
              selectedTime={selectedTime}
              onChooseTime={handleOpenTimeSlots}
              onChooseDifferentTime={handleOpenReschedule}
              availableTips={availableTips}
              canAddTip={canAddTip}
              onSelectTip={handleSelectTip}
              tipLoading={isUpdatingTip}
            />
          </View>

          {/* Shop Information Card */}
          <ShopDirections
            shopName={shop.name}
            address={`${shop.address.street}, ${shop.address.city}, ${shop.address.state} ${shop.address.zip}`}
            staticMapUrl={shop.staticMap?.url}
            latitude={shop.location?.coordinates[0] ?? null}
            longitude={shop.location?.coordinates[1] ?? null}
          />

          {/* CTA Buttons */}
          {uiState === 'selected' && (
            <Button variant="primary" onPress={handleBook} loading={isBooking}>
              {t('actions.book')}
            </Button>
          )}
        </VStack>
      </ScrollView>

      {/* Bottom Sheets */}
      {slotTimes && slotTimes.length > 0 && (
        <WaitlistTimeSlotsSheet
          ref={timeSlotsSheetRef}
          timeSlots={slotTimes}
          timezone={shop.timezone}
          formattedDate={formattedDate}
          barberName={barberName}
          barberPhotoUrl={barberPhotoUrl}
          serviceName={waitlistData.services?.[0]?.name ?? ''}
          onConfirm={handleSlotConfirm}
        />
      )}
    </>
  );
};

ViewWaitlistContentWrapper.displayName = 'ViewWaitlistContentWrapper';
export { ViewWaitlistContentWrapper };
